/**
 * ConnectorAssignTenantsSlideout — V226 + V227 / CONN-SCOPE.
 *
 * Row-level "Assign to tenants" action for BOTH connector list pages:
 *   • Build > Connectors (generic connectors)
 *   • External Agent Frameworks (platform connectors)
 *
 * Composition:
 *   • Slideout (V2 §5.5 / §3.13 — configurator pattern)
 *   • Sticky header with connector name + owner indicator
 *   • Body: TenantScopePicker prepopulated with the connector's CURRENT
 *     grants (cascade OR per-instance, V226). Owner tenant pinned + non-removable.
 *   • Footer: Cancel + Save. Save diffs cascade/per-instance changes and
 *     dispatches the minimal set of BE calls.
 *
 * Diff strategy (V226):
 *   - Build {newByTenant, oldByTenant} maps keyed by tenantId.
 *   - For tenants in NEW (regardless of OLD state): include in a single
 *     POST grants[]; BE additively handles cascade↔specific switching via
 *     its mutual-exclusion logic. We send the FULL desired grant for each
 *     touched tenant (cascade row OR exact per-instance set).
 *   - For tenants in OLD but not NEW: per-tenant DELETE (revoke ALL active
 *     rows for that tenant).
 *
 * Per-instance-only deletions (tenant stays but loses one of N instances)
 * are handled by sending the new (shorter) per-instance list via the
 * grants POST — BE's mutual exclusion + per-instance idempotency revokes
 * the disappeared rows. We don't need to issue separate per-instance
 * DELETEs in that case.
 *
 * Props
 *   isOpen                  — visibility
 *   onClose                 — close callback (no-op while busy)
 *   onSaved                 — called after successful save with new grants
 *   connector               — { id, name, tenantId }
 *   currentScope            — current grants: rich `[{tenantId, instanceIds}]`
 *                             OR legacy `string[]` (auto-coerced to cascade-only)
 *   orgId                   — caller's orgId (for the picker's tenant fetch)
 *   apiAssignGrants         — (connectorId, grants) => Promise — POST .../scope-tenants
 *                             with {grants: [...]} body
 *   apiRevokeTenant         — (connectorId, tenantId) => Promise — DELETE .../{tenantId}
 *                             (revoke ALL active rows for that tenant)
 *   resourceLabel           — display label, e.g. "External Agent Framework"
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Users, Save as SaveIcon, Lock } from 'lucide-react';
import { Slideout, Button } from './index';
import TenantScopePicker from './TenantScopePicker';
import { useToast } from '../../contexts/ToastContext';

/** Coerce legacy string[] OR rich [{tenantId, instanceIds}] into rich. */
function normalizeScope(scope) {
  if (!Array.isArray(scope)) return [];
  return scope
    .map((entry) => {
      if (typeof entry === 'string') return { tenantId: entry, instanceIds: [] };
      if (entry && typeof entry === 'object' && entry.tenantId) {
        return {
          tenantId: entry.tenantId,
          instanceIds: Array.isArray(entry.instanceIds) ? entry.instanceIds.slice().sort() : [],
        };
      }
      return null;
    })
    .filter(Boolean);
}

/** Equality on a single grant — tenantId + canonical-sorted instanceIds. */
function grantsEqual(a, b) {
  if (a.tenantId !== b.tenantId) return false;
  const ai = a.instanceIds || [];
  const bi = b.instanceIds || [];
  if (ai.length !== bi.length) return false;
  const as = [...ai].sort();
  const bs = [...bi].sort();
  for (let i = 0; i < as.length; i++) if (as[i] !== bs[i]) return false;
  return true;
}

export default function ConnectorAssignTenantsSlideout({
  isOpen,
  onClose,
  onSaved,
  connector,
  /* RC-75 — Explicit override for the id passed to the assign/revoke
   * APIs. Backstory: callers used to be ConnectorsPageV2 (marketplace
   * page where row.id is the manifest string like "anthropic") and
   * GelPlatformConnectorsPageV2 (installed-only page where row.id IS
   * the instance UUID). The slideout passed `connector.id` straight
   * through to the BE — which always expects a UUID — so the
   * marketplace caller hit "Path or query parameter 'connectorId'
   * could not be parsed as UUID" the moment the user clicked Save.
   *
   * Rather than ask every caller to mutate `connector.id`, we let
   * them prop in a `connectorId` that takes precedence. Falls back
   * to `connector?.id` so the GelPlatform caller continues to work
   * unchanged. The marketplace caller now passes
   * `instance?.id || id` explicitly. */
  connectorId,
  currentScope = [],
  orgId = null,
  apiAssignGrants,
  apiRevokeTenant,
  resourceLabel = 'Connector',
}) {
  const { addToast } = useToast();
  const [draft, setDraft] = useState(() => normalizeScope(currentScope));
  const [busy, setBusy] = useState(false);
  const resolvedConnectorId = connectorId || connector?.id;

  // Reset draft when a new row opens. (Without this, switching rows
  // would carry over the prior connector's selection.)
  //
  // Deps gate intentionally limited to [isOpen, connector?.id]: currentScope
  // gets a fresh array identity on every parent render (the page maps
  // connectors → JSX each render), so including it here would re-fire the
  // reset on every render and wipe in-flight edits. The connector.id is
  // the stable "which row is this" key — when THAT changes we genuinely
  // do want to reseed from the new row's scope. This project doesn't have
  // the react-hooks ESLint plugin loaded, so we can't use the targeted
  // disable comment — plain prose explanation has to suffice.
  useEffect(() => {
    if (!isOpen) return;
    setDraft(normalizeScope(currentScope));
  }, [isOpen, resolvedConnectorId]);

  // Diff baseline vs draft. Drives the unsaved indicator + minimal API calls.
  const { addedOrChanged, removed, dirty } = useMemo(() => {
    const baseline = normalizeScope(currentScope);
    const baselineByTenant = new Map(baseline.map((g) => [g.tenantId, g]));
    const draftByTenant = new Map(draft.map((g) => [g.tenantId, g]));

    const changed = [];
    draft.forEach((g) => {
      const prior = baselineByTenant.get(g.tenantId);
      if (!prior || !grantsEqual(prior, g)) changed.push(g);
    });
    const removedIds = [];
    baseline.forEach((g) => {
      if (!draftByTenant.has(g.tenantId)) removedIds.push(g.tenantId);
    });
    return {
      addedOrChanged: changed,
      removed: removedIds,
      dirty: changed.length + removedIds.length > 0,
    };
  }, [currentScope, draft]);

  const handleSave = async () => {
    if (!resolvedConnectorId) return;
    if (typeof apiAssignGrants !== 'function' || typeof apiRevokeTenant !== 'function') return;
    if (!dirty) { onClose?.(); return; }
    setBusy(true);
    try {
      const ops = [];
      if (addedOrChanged.length > 0) {
        // ONE POST per save — BE handles per-tenant cascade↔specific switching
        // and per-instance idempotency. We pass the FULL desired grant per
        // touched tenant; untouched tenants are not affected by this call.
        ops.push(apiAssignGrants(resolvedConnectorId, addedOrChanged.map((g) => ({
          tenantId: g.tenantId,
          instanceIds: g.instanceIds || [],
        }))));
      }
      removed.forEach((tid) => ops.push(apiRevokeTenant(resolvedConnectorId, tid)));
      const results = await Promise.allSettled(ops);
      const failures = results.filter((r) => r.status === 'rejected'
        || (r.value && r.value.ok === false));
      if (failures.length > 0) {
        const first = failures[0];
        const msg = first?.reason?.message
          || first?.value?.error
          || `${failures.length} grant operation${failures.length === 1 ? '' : 's'} failed`;
        addToast({ tone: 'danger', title: 'Some changes failed', description: msg });
      } else {
        addToast({
          tone: 'success',
          title: 'Tenant scope updated',
          description: buildSummary(addedOrChanged, removed),
        });
        if (typeof onSaved === 'function') onSaved(draft);
        onClose?.();
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Update failed', description: e?.message || String(e) });
    } finally {
      setBusy(false);
    }
  };

  const header = (
    <div className="px-4 py-3 border-b border-v2-border-subtle">
      <div className="flex items-center gap-2 mb-1">
        <Users size={14} className="text-v2-action" aria-hidden="true" />
        <span className="text-[13px] font-semibold text-v2-ink">Assign to tenants</span>
      </div>
      <div className="text-[11px] text-v2-ink-muted leading-snug">
        {resourceLabel} · <strong className="text-v2-ink">{connector?.name || '(unnamed)'}</strong>
      </div>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-v2-border-subtle bg-v2-page">
      <div className="text-[10.5px] text-v2-ink-muted">
        {dirty
          ? <>
              {addedOrChanged.length > 0 && <span className="text-v2-success-strong">~{addedOrChanged.length} </span>}
              {removed.length > 0 && <span className="text-v2-danger-strong">−{removed.length} </span>}
              unsaved
            </>
          : 'No unsaved changes'}
      </div>
      <div className="flex items-center gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="primary"
          onClick={handleSave}
          loading={busy}
          disabled={!dirty}
          iconLeft={<SaveIcon size={12} />}
        >
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <Slideout
      isOpen={!!isOpen}
      onClose={busy ? null : onClose}
      dismissOnBackdrop={!busy}
      width={560}
      ariaLabel="Assign connector to tenants"
      header={header}
      footer={footer}
    >
      <div className="px-4 py-4 flex flex-col gap-4">
        <div className="text-[12px] text-v2-ink leading-relaxed">
          Pick the tenants that should be able to <strong>list and use</strong> this {resourceLabel.toLowerCase()}.
          Shared tenants get <strong>read + use</strong> access only — they cannot edit
          credentials, rotate, change the endpoint, or delete.
        </div>

        <div className="rounded-v2-md bg-v2-muted text-[11px] text-v2-ink-muted leading-snug p-3 border border-v2-border-subtle">
          <div className="flex items-center gap-1.5 mb-1 text-v2-ink">
            <Lock size={11} aria-hidden="true" />
            <strong className="font-semibold">Owner-tenant edit rights are reserved.</strong>
          </div>
          Only the owner tenant (the one that created this {resourceLabel.toLowerCase()}) can rotate
          credentials, change auth, or delete it. Sharing here doesn't transfer those
          rights — it only adds visibility for cross-tenant use cases. Per-instance
          grants let you restrict a tenant's access to specific environments
          (e.g., share with QA-Staging only, not QA-Production).
        </div>

        <TenantScopePicker
          value={draft}
          onChange={setDraft}
          ownerTenantId={connector?.tenantId}
          orgId={orgId}
          readOnly={busy}
        />
      </div>
    </Slideout>
  );
}

function buildSummary(addedOrChanged, removed) {
  const parts = [];
  if (addedOrChanged.length > 0) {
    const cascade = addedOrChanged.filter((g) => !g.instanceIds || g.instanceIds.length === 0).length;
    const specific = addedOrChanged.length - cascade;
    const bits = [];
    if (cascade > 0) bits.push(`${cascade} cascade`);
    if (specific > 0) bits.push(`${specific} per-instance`);
    parts.push(`updated ${addedOrChanged.length} tenant${addedOrChanged.length === 1 ? '' : 's'} (${bits.join(', ')})`);
  }
  if (removed.length > 0) parts.push(`revoked ${removed.length} tenant${removed.length === 1 ? '' : 's'}`);
  if (parts.length === 0) return 'No changes';
  return parts.join(', ') + '.';
}
